import { useEffect, useState } from 'react';
import { EventPageType } from '@graphql/generated-base';

import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useIsAdminGuestSitePreview } from '@shared/hooks/useIsAdminGuestSitePreview';

/**
 * Whether a page is the Wedding Map page.
 *
 * This compared the string `'map'` until 2026-10-29, because the enum member
 * ships from event_service (TRAV-5231) and `verify-gql-schema` validates joy-web
 * against the *live prod* schema — so naming it failed the build until that
 * service deployed. It reached prod on 2026-10-26 and joy-web has regenerated,
 * so this is the real enum now and the stand-in is gone.
 */
export const isMapPageType = (pageType: EventPageType): boolean => pageType === EventPageType.map;

/**
 * Whether this guest site is the website editor's preview, opened by a couple
 * in the map editor's rollout (`weddingMapEditorRollout`).
 *
 * The editor can roll out ahead of the guest map, and without this its preview
 * would drop the map page like any guest's site and show none of the edits.
 *
 * The preview is told apart by its `?preview=true` / `ctx` params
 * (`useIsAdminGuestSitePreview`), not by being framed: Carbon embeds and
 * masked or custom domains frame the guest site too, and those are real guest
 * visits that must not see the map page before `guestWeddingMapRollout`.
 *
 * Decided after mount, because the server cannot tell it is framed and the
 * first client render must match it. The editor flag is read only in the
 * preview and records no exposure, so no guest ever reads it.
 */
export const useIsWeddingMapEditorPreview = (): boolean => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const isAdminPreview = useIsAdminGuestSitePreview();
  const isPreview = isMounted && isAdminPreview;
  const [isEditorEnabled] = useIsFeatureEnabled('weddingMapEditorRollout', { skip: !isPreview, shouldTrackExposure: false });
  return isPreview && isEditorEnabled;
};

/**
 * Whether the Wedding Map page may be shown to this guest at all.
 *
 * Also true in the map editor's preview; see `useIsWeddingMapEditorPreview`.
 *
 * **Records no exposure.** This read only decides whether an entry is listed,
 * and it runs on every guest-site page view through `useGuestSitePages` — so
 * tracking it would count every guest of every event as exposed to
 * `guestWeddingMapRollout`, whether or not the event even has a map page, and
 * the experiment would measure nothing. The surfaces that actually show the
 * map — the Travel section and the `map` page itself — read the flag again
 * without this option, which is where the exposure belongs.
 *
 * While the flag resolves this reads false, so the page stays out of the list
 * until the guest is known to be in the rollout. Listing it and then removing
 * it would shift the nav under them.
 */
export const useIsMapPageVisible = (): boolean => {
  const [isEnabled] = useIsFeatureEnabled('guestWeddingMapRollout', { shouldTrackExposure: false });
  const isEditorPreview = useIsWeddingMapEditorPreview();
  return isEnabled || isEditorPreview;
};

/**
 * Drops the Wedding Map page from a list unless the guest is in the rollout.
 *
 * Returns the **same array** when nothing is removed, which is every event
 * today. Several callers key effects on that reference — `useGuestSitePages`
 * and `useAlohaScroll` (LEGRSVP-85) — so a filter that always allocates hands
 * them a fresh array on every render and defeats the pin. Shared so the two
 * call sites cannot drift on that.
 */
export const withoutHiddenMapPage = <T extends { type: EventPageType }>(pages: ReadonlyArray<T>, isMapPageVisible: boolean): ReadonlyArray<T> => {
  if (isMapPageVisible) {
    return pages;
  }
  const next = pages.filter(page => !isMapPageType(page.type));
  return next.length === pages.length ? pages : next;
};
